import { Link } from "react-router-dom";
import { Spin, Tooltip } from "antd";
import {
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  PauseCircleOutlined,
  QuestionCircleOutlined,
  StopOutlined,
  UserDeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import { toPersianNumber } from "../../utils/helpers";
import styles from "./styles.module.css";

const PROJECTS_REPORT = "/reports/projects-performance-report";

/** Live statuses, i.e. everything except "done". Mirrors Project::STATUSES. */
const OPEN_PROJECT_STATUSES = "1,2";

const shiftDate = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * Every task counter links to the task list filtered by the same slice the API
 * counted, so the chip's number and the list's row count are the same query.
 *
 * They used to point at the projects performance report — a page of charts. A
 * manager who clicked "۷ تسک راکد" expecting seven rows had to go hunting
 * through projects by hand, which is the exact work the chip promised to remove.
 * Two of the personal chips had no link at all.
 *
 * `scope=org` is what widens the list from the signed-in user's tasks to the
 * whole organization; the API grants it only to someone who manages tasks,
 * projects or the organization, which is the same audience that sees the org
 * counters in the first place.
 */
const taskSlice = (params, orgWide) =>
  /* Two routes, not one page with a mode: «همه تسک‌ها» is the organization-wide
     list and «تسک‌های من» is the personal one, so the page a chip opens says
     whose tasks it is showing in its own title. */
  `${orgWide ? "/all-tasks" : "/my-tasks"}?${new URLSearchParams(
    params,
  ).toString()}`;

const ORG = true;

/**
 * The counters that turn the dashboard from a scoreboard into a worklist. Order
 * is deliberate: the things that are already late come before the things that
 * are merely untidy — and `tone` now follows that same order down the shared
 * semantic ramp (critical → warn → caution → idle) instead of assigning each
 * counter a colour of its own. Colour is the severity here, not a category.
 */
export const ORG_ATTENTION_ITEMS = [
  {
    key: "overdue_tasks",
    label: "تسک عقب‌افتاده",
    tone: "critical",
    icon: <WarningOutlined />,
    hint: "تسک‌هایی که هنوز تمام نشده‌اند و تاریخ موعدشان گذشته است.",
    href: taskSlice({ overdue: 1, order_by: "due_date" }, ORG),
  },
  {
    key: "projects_overdue",
    label: "پروژه عقب‌افتاده",
    tone: "critical",
    icon: <AlertOutlined />,
    hint: "پروژه‌های باز که از تاریخ پایانشان گذشته است.",
    href: `/projects?statuses=${OPEN_PROJECT_STATUSES}&due_date_to=${shiftDate(-1)}`,
  },
  {
    /* The only counter with no list of its own: "behind schedule" compares
       progress against elapsed time, which the projects report is built to show
       and a flat list is not. It keeps pointing there, on purpose. */
    key: "projects_behind",
    label: "پروژه عقب از برنامه",
    tone: "warn",
    icon: <ClockCircleOutlined />,
    hint: "پیشرفت واقعی این پروژه‌ها از سهمِ سپری‌شده‌ی زمان‌بندی کمتر است.",
    href: PROJECTS_REPORT,
  },
  {
    key: "blocked_tasks",
    label: "تسک بلاک‌شده",
    tone: "warn",
    icon: <StopOutlined />,
    hint: "تسک‌هایی که منتظر تسک دیگری هستند یا در ستون «بلاک/معلق» مانده‌اند.",
    href: taskSlice({ blocked: 1, order_by: "updated_at" }, ORG),
  },
  {
    key: "stale_tasks",
    label: "تسک راکد",
    tone: "warn",
    icon: <PauseCircleOutlined />,
    hint: "تسک‌های بازی که بیش از ۷ روز است هیچ تغییری نکرده‌اند.",
    href: taskSlice({ stale: 1, order_by: "updated_at" }, ORG),
  },
  {
    key: "due_soon_tasks",
    label: "ددلاین این هفته",
    tone: "caution",
    icon: <CalendarOutlined />,
    hint: "تسک‌های بازی که موعدشان تا ۷ روز آینده است.",
    href: taskSlice({ due_soon: 1, order_by: "due_date" }, ORG),
  },
  {
    key: "projects_due_soon",
    label: "پروژه با ددلاین نزدیک",
    tone: "caution",
    icon: <CalendarOutlined />,
    hint: "پروژه‌های بازی که تا ۷ روز آینده باید تمام شوند.",
    href: `/projects?statuses=${OPEN_PROJECT_STATUSES}&due_date_from=${shiftDate(0)}&due_date_to=${shiftDate(7)}`,
  },
  {
    key: "unassigned_tasks",
    label: "بدون مسئول",
    tone: "idle",
    icon: <UserDeleteOutlined />,
    hint: "تسک‌های بازی که به هیچ‌کس اختصاص داده نشده‌اند.",
    href: taskSlice({ unassigned: 1, order_by: "created_at" }, ORG),
  },
  {
    key: "no_due_date_tasks",
    label: "بدون موعد",
    tone: "idle",
    icon: <QuestionCircleOutlined />,
    hint: "تسک‌های بازی که تاریخ موعد ندارند و در هیچ گزارش زمان‌بندی دیده نمی‌شوند.",
    href: taskSlice({ no_due_date: 1, order_by: "created_at" }, ORG),
  },
];

/**
 * The personal counterpart. Same counters, same definitions — the endpoint scopes
 * them to the signed-in user — worded in the first person and pointing at the
 * same slices of the same list, without the org scope.
 */
export const USER_ATTENTION_ITEMS = [
  {
    key: "overdue_tasks",
    label: "عقب‌افتاده",
    tone: "critical",
    icon: <WarningOutlined />,
    hint: "تسک‌های تو که هنوز تمام نشده‌اند و موعدشان گذشته است.",
    href: taskSlice({ overdue: 1, order_by: "due_date" }),
  },
  {
    key: "due_soon_tasks",
    label: "ددلاین این هفته",
    tone: "caution",
    icon: <CalendarOutlined />,
    hint: "تسک‌های تو که موعدشان تا ۷ روز آینده است.",
    href: taskSlice({ due_soon: 1, order_by: "due_date" }),
  },
  {
    key: "blocked_tasks",
    label: "بلاک‌شده",
    tone: "warn",
    icon: <StopOutlined />,
    hint: "تسک‌هایی که منتظر تسک دیگری هستند یا در ستون «بلاک/معلق» مانده‌اند.",
    href: taskSlice({ blocked: 1, order_by: "updated_at" }),
  },
  {
    key: "stale_tasks",
    label: "راکد",
    tone: "warn",
    icon: <PauseCircleOutlined />,
    hint: "تسک‌های بازی که بیش از ۷ روز است دست نخورده‌اند.",
    href: taskSlice({ stale: 1, order_by: "updated_at" }),
  },
  {
    key: "no_due_date_tasks",
    label: "بدون موعد",
    tone: "idle",
    icon: <QuestionCircleOutlined />,
    hint: "تسک‌های بازی که موعد ندارند؛ در هیچ برنامه‌ریزی زمانی دیده نمی‌شوند.",
    href: taskSlice({ no_due_date: 1, order_by: "created_at" }),
  },
];

const Chip = ({ item, value }) => {
  const body = (
    <>
      <span className={`${styles.chipIcon} kpi-icon-${item.tone}`}>
        {item.icon}
      </span>
      <span className={styles.chipText}>
        <span className={styles.chipValue}>{toPersianNumber(value)}</span>
        <span className={styles.chipLabel}>{item.label}</span>
      </span>
    </>
  );

  return (
    <Tooltip title={item.hint} trigger={["hover", "focus"]}>
      {item.href ? (
        <Link to={item.href} className={styles.chip}>
          {body}
        </Link>
      ) : (
        <span className={styles.chip}>{body}</span>
      )}
    </Tooltip>
  );
};

/**
 * Counters are hidden when they are zero: a wall of zeros reads as noise and
 * buries the one number that is not zero. When every counter is zero the bar
 * says so explicitly instead of collapsing, so the section never looks broken.
 */
/**
 * The one thing above the fold that is allowed to shout.
 *
 * It used to sit in the same white card, with the same border and padding, as
 * the thirty-day activity feed beside it — a dashboard where everything looks
 * equally important tells the reader nothing. Now it has two states and they
 * look nothing alike: when something needs attention it is a tinted band with
 * numerals big enough to read from across a desk; when nothing does, it
 * collapses to a single quiet line and gets out of the way.
 *
 * The tint is the organization's accent, not a risk colour. Most organizations
 * are behind on something most of the time, so a band that turns red whenever a
 * counter is non-zero is red permanently — and a dashboard that is always red
 * is a dashboard nobody reads. Severity still shows, per counter, on the chip
 * icons, where it distinguishes one number from another instead of shouting
 * about all of them at once.
 */
const AttentionBar = ({
  attention,
  loading = false,
  items = ORG_ATTENTION_ITEMS,
  title = "نیازمند رسیدگی",
  note = "مستقل از بازه‌ی گزارش",
  clearText = "هیچ مورد عقب‌افتاده یا رهاشده‌ای نیست؛ همه چیز اوکیه.",
  dataTour,
}) => {
  const visible = items.filter((item) => (attention?.[item.key] ?? 0) > 0);
  const allClear = !loading && attention && visible.length === 0;

  if (allClear) {
    return (
      <div className={`${styles.clearLine} ds-section`} data-tour={dataTour}>
        <CheckCircleOutlined className={styles.clearIcon} />
        <span>{clearText}</span>
      </div>
    );
  }

  return (
    <div
      /* `ds-section` carries the spacing every block on the dashboard uses; the
         band lost it in the rewrite and sat flush against the section below. */
      className={`${styles.wrap} ds-section`}
      data-tour={dataTour}
    >
      <div className={styles.header}>
        <span className={styles.headIcon}>
          <AlertOutlined />
        </span>
        <div className={styles.headText}>
          <h6 className={styles.title}>{title}</h6>
          {note && <span className={styles.note}>{note}</span>}
        </div>
      </div>

      {loading && !attention ? (
        /* A spinner, not skeleton blocks. The band is tinted, so grey
           placeholders on it read as content that failed to load rather than as
           content on the way. */
        <div className={styles.loading}>
          <Spin size="small" />
          <span>در حال بررسی وضعیت…</span>
        </div>
      ) : (
        <div className={styles.chips}>
          {visible.map((item) => (
            <Chip key={item.key} item={item} value={attention[item.key]} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AttentionBar;
